import { h } from 'preact';
import { useState } from 'preact/hooks';
import { GraphEdge, VertexPreview } from '../../types/graph';
import { getVertexUrl } from '../../services/vertex';
import { route } from '../HashRouter';
import { EdgeInfoModal } from './EdgeInfoModal';
import { VertexSquareCard } from '../shared/VertexSquareCard';
import { DeleteIcon } from '../icons';

export interface EdgeListProps {
  edges: GraphEdge[];
  previews: Record<string, VertexPreview>;
  direction: 'incoming' | 'outgoing';
  currentVertexUrl: string;  // Current vertex URL (to add to trail when navigating)
  currentTrail?: string;     // Current breadcrumb trail
  onDelete: (edge: GraphEdge) => void;
  onUpdateInfo: (edge: GraphEdge, info: string | null) => Promise<void>;
}

export function EdgeList({ edges, previews, direction, currentVertexUrl, currentTrail, onDelete, onUpdateInfo }: EdgeListProps) {
  const [editingEdge, setEditingEdge] = useState<{
    edge: GraphEdge;
    preview: VertexPreview | undefined;
    vertexUrl: string;
  } | null>(null);

  const getEdgeKey = (edge: GraphEdge) => `${edge.source.rendered}-${edge.target.rendered}`;

  const handleCardClick = (targetUrl: string) => {
    // Build new trail: current trail + current vertex
    const newTrail = currentTrail
      ? `${currentTrail},${encodeURIComponent(currentVertexUrl)}`
      : encodeURIComponent(currentVertexUrl);

    // Set `from` to the current URL so Back returns to this graph (and, via
    // that URL's own `from` param, chain-walks further back).
    const encodedFrom = encodeURIComponent(window.location.hash.slice(1));
    route(`/graph/${encodeURIComponent(targetUrl)}?trail=${newTrail}&from=${encodedFrom}`);
  };

  const handleEditClick = (e: MouseEvent, edge: GraphEdge, preview: VertexPreview | undefined, vertexUrl: string) => {
    e.stopPropagation();
    setEditingEdge({ edge, preview, vertexUrl });
  };

  const handleCloseModal = () => {
    setEditingEdge(null);
  };

  console.log('[EdgeList] Rendering:', { direction, edgesCount: edges.length, edges, previews });

  if (edges.length === 0) {
    return (
      <div class="graph-edges-row">
        <p class="graph-empty-message">
          {direction === 'outgoing' ? 'No references yet.' : 'Not referenced by any vertices yet.'}
        </p>
      </div>
    );
  }

  return (
    <div class="graph-edges-row">
      {edges.map((edge) => {
        const displayVertex = direction === 'outgoing' ? edge.target : edge.source;
        const vertexUrl = getVertexUrl(displayVertex);
        const preview = vertexUrl ? previews[vertexUrl] : undefined;
        const edgeKey = getEdgeKey(edge);

        return (
          <VertexSquareCard
            key={edgeKey}
            url={vertexUrl!}
            preview={preview}
            onClick={() => handleCardClick(vertexUrl!)}
            extraContent={edge.info && <div class="graph-edge-card-info">{edge.info}</div>}
            actions={
              <>
                <button
                  class="graph-edge-card-edit"
                  onClick={(e) => handleEditClick(e as MouseEvent, edge, preview, vertexUrl!)}
                  title="Edit note"
                >
                  &#9998;
                </button>
                {edge.edgeType === 'UserCreated' && (
                  <button
                    class="graph-edge-card-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(edge);
                    }}
                    title="Delete this reference"
                  >
<DeleteIcon size={14} />
                  </button>
                )}
              </>
            }
          />
        );
      })}

      {/* Modal for editing edge info */}
      {editingEdge && (
        <EdgeInfoModal
          edge={editingEdge.edge}
          preview={editingEdge.preview}
          vertexUrl={editingEdge.vertexUrl}
          onClose={handleCloseModal}
          onUpdateInfo={async (edge, info) => {
            await onUpdateInfo(edge, info);
            // Refresh the edge data in modal state
            setEditingEdge(prev => prev ? { ...prev, edge: { ...prev.edge, info } } : null);
          }}
        />
      )}
    </div>
  );
}
